import { useCallback } from 'react'
import {
    collection,
    doc,
    getDoc,
    getDocs,
    setDoc,
    updateDoc,
    query,
    where,
    orderBy,
    limit,
    Timestamp,
    serverTimestamp,
} from 'firebase/firestore'
import { firestore, functions, auth } from '../config/firebase'
import { httpsCallable } from 'firebase/functions'
import { IVehicle, Vehicle, VehicleStatus } from '../../../shared/types/vehicle.types'
import { COLLECTIONS } from '../../../shared/constants/collections'

export interface VehicleFilters {
    /** Filtruje dle stavu — jeden status nebo pole statusů (použije 'in' operátor). */
    status?: VehicleStatus | VehicleStatus[]
    /** Filtruje vozidla registrovaná konkrétním uživatelem (UID). */
    createdBy?: string
    /** Filtruje vozidla přiřazená konkrétní garáži (UID). */
    garageUid?: string
}

type VehicleUpdater = Partial<Omit<IVehicle, 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy'>>

interface IUseVehicleReturn {
    get: (uid: string) => Promise<Vehicle | null>
    list: (filters?: VehicleFilters) => Promise<Vehicle[]>
    searchByVinPrefix: (prefix: string, maxResults?: number) => Promise<Vehicle[]>
    create: (data: Omit<IVehicle, 'createdAt'>) => Promise<Vehicle>
    edit: (uid: string, data: VehicleUpdater) => Promise<void>
    checkVIN: (vin: string) => Promise<boolean>
}

const convertDoc = (id: string, data: Record<string, unknown>): Vehicle => ({
    uid: id,
    ...data,
    createdAt: data.createdAt instanceof Timestamp
        ? data.createdAt.toDate()
        : new Date(data.createdAt as string),
} as Vehicle)

const useVehicle = (): IUseVehicleReturn => {

    /**
     * Načte jedno vozidlo podle jeho UID.
     * @param uid - Firestore document ID vozidla
     */
    const get = useCallback(async (uid: string): Promise<Vehicle | null> => {
        const snapshot = await getDoc(doc(firestore, COLLECTIONS.VEHICLE, uid))
        if (!snapshot.exists()) return null
        return convertDoc(snapshot.id, snapshot.data())
    }, [])

    /**
     * Načte seznam vozidel s volitelnými filtry.
     * Výsledky jsou řazeny sestupně dle data registrace.
     * @param filters - Volitelné filtry: status, createdBy, garageUid
     */
    const list = useCallback(async (filters?: VehicleFilters): Promise<Vehicle[]> => {
        const constraints = []

        if (filters?.status !== undefined) {
            if (Array.isArray(filters.status)) {
                constraints.push(where('status', 'in', filters.status))
            } else {
                constraints.push(where('status', '==', filters.status))
            }
        }

        if (filters?.createdBy) {
            constraints.push(where('createdBy', '==', filters.createdBy))
        }

        if (filters?.garageUid) {
            constraints.push(where('garageUid', '==', filters.garageUid))
        }

        constraints.push(orderBy('createdAt', 'desc'))

        const q = query(collection(firestore, COLLECTIONS.VEHICLE), ...constraints)
        const snapshot = await getDocs(q)
        return snapshot.docs.map((d) => convertDoc(d.id, d.data()))
    }, [])

    /**
     * Vyhledá vozidla, jejichž VIN začíná zadaným prefixem (case-insensitive uppercase).
     * @param prefix - Začátek VIN kódu (min. 1 znak)
     * @param maxResults - Maximální počet výsledků (výchozí 20)
     */
    const searchByVinPrefix = useCallback(async (prefix: string, maxResults = 20): Promise<Vehicle[]> => {
        const upper = prefix.toUpperCase()
        const q = query(
            collection(firestore, COLLECTIONS.VEHICLE),
            where('vin', '>=', upper),
            where('vin', '<=', upper + '\uf8ff'),
            limit(maxResults),
        )
        const snapshot = await getDocs(q)
        return snapshot.docs.map((d) => convertDoc(d.id, d.data()))
    }, [])

    /**
     * Vytvoří nové vozidlo. Pole `createdAt` je nastaveno serverem.
     * @param data - Data vozidla bez `createdAt`
     */
    const create = useCallback(async (data: Omit<IVehicle, 'createdAt'>): Promise<Vehicle> => {
        const ref = doc(firestore, COLLECTIONS.VEHICLE, data.vin)
        await setDoc(ref, { ...data, createdAt: serverTimestamp() })
        return { uid: data.vin, ...data, createdAt: new Date() }
    }, [])

    /**
     * Aktualizuje pole existujícího vozidla.
     * @param uid - Firestore document ID vozidla
     * @param data - Pole k aktualizaci (bez `createdAt` a `createdBy`)
     */
    const edit = useCallback(async (uid: string, data: VehicleUpdater): Promise<void> => {
        await updateDoc(doc(firestore, COLLECTIONS.VEHICLE, uid), {
            ...data,
            updatedAt: serverTimestamp(),
            updatedBy: auth.currentUser?.uid ?? null,
        })
    }, [])

    /**
     * Ověří, zda vozidlo s daným VIN kódem již existuje (přes Cloud Function).
     * Řeší se přes funkci, protože vozidlo může existovat v jiné garáži.
     * @param vin - VIN kód vozidla
     */
    const checkVIN = useCallback(async (vin: string): Promise<boolean> => {
        const fn = httpsCallable<{ vin: string }, { exists: boolean }>(functions, 'vehicleCheckVIN')
        const result = await fn({ vin })
        return result.data.exists
    }, [])

    return { get, list, searchByVinPrefix, create, edit, checkVIN }
}

export default useVehicle
